import { attachInstruction, extractInstruction } from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { colors } from '~/styles';

const Styled = {
  Node: styled.div`
    position: relative;
  `,

  Tooltip: styled.div`
    background-color: ${colors.white};
    border: 1px solid ${colors.red};
    color: ${colors.danger};
    padding: 0.375rem 1rem;
    border-radius: 4px;
    font-size: 0.8rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    pointer-events: none;
  `,
};

export default function Node({ node, depth = 0, lastInGroup = false, block = false, renderNode, renderDragPreview }) {
  const [dragElement, setDragElement] = useState(null);
  const [dropElement, setDropElement] = useState(null);
  const [instruction, setInstruction] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragState, setDragState] = useState({ type: 'idle' });

  const virtualRef = useRef({
    getBoundingClientRect: () => ({
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      width: 0,
      height: 0,
    }),
  });

  const [tooltipEl, setTooltipEl] = useState(null);
  const { styles, attributes, update } = usePopper(virtualRef.current, tooltipEl, {
    placement: 'top-start',
    strategy: 'fixed',
    modifiers: [{ name: 'offset', options: { offset: [12, 0] } }],
  });

  useEffect(() => {
    if (!dragElement || !dropElement) return;

    return combine(
      draggable({
        element: dragElement,
        getInitialData: () => ({
          type: 'node',
          id: node.id,
          parentId: node.parentId,
          isBillable: node.isBillable,
        }),
        onGenerateDragPreview({ nativeSetDragImage }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({ x: 8, y: 8 }),
            render({ container }) {
              setDragState({ type: 'preview', container });
              return () => setDragState({ type: 'dragging' });
            },
          });
        },
        onDragStart: () => setIsDragging(true),
        onDrop: () => {
          setIsDragging(false);
          setDragState({ type: 'idle' });
        },
      }),
      dropTargetForElements({
        element: dropElement,
        getData: ({ input, element, source }) => {
          const isSelf = source?.data?.type === 'node' && source.data.id === node.id;

          const mode =
            node.children.length > 0 && node.expanded ? 'expanded' : lastInGroup ? 'last-in-group' : 'standard';

          const isBillableUnderNonBillable =
            source?.data?.type === 'node' &&
            source.data.id !== node.id &&
            source.data.isBillable &&
            node.isBillable === false;

          let blockedInstructions = null;
          let blockReason = null;

          if (block) {
            blockedInstructions = ['reorder-above', 'make-child', 'reorder-below', 'reparent'];
            blockReason = `A task can't be moved into its own subtask group.`;
          } else if (isSelf) {
            blockedInstructions = ['reorder-above', 'reorder-below', 'make-child'];
            blockReason = `A task can't be moved relative to itself.`;
          } else if (isBillableUnderNonBillable) {
            blockedInstructions = ['make-child', 'reorder-above', 'reorder-below'];
            blockReason = `A billable task can't be nested under a non-billable task.`;
          }

          return attachInstruction(
            { type: 'node', id: node.id, parentId: node.parentId, blockReason },
            {
              input,
              element,
              currentLevel: depth,
              indentPerLevel: 32,
              mode,
              block: blockedInstructions,
            },
          );
        },
        getIsSticky: () => true,
        onDragEnter: (args) =>
          setInstruction({ ...extractInstruction(args.self.data), blockedReason: args.self.data.blockReason }),
        onDrag: (args) => {
          const instruction = extractInstruction(args.self.data);

          if (args.self.data.blockReason) {
            const { location } = args;
            const input = location?.current?.input;
            if (input?.clientX && input?.clientY) {
              const input = location?.current?.input;
              virtualRef.current.getBoundingClientRect = () => ({
                top: input.clientY,
                bottom: input.clientY,
                left: input.clientX,
                right: input.clientX,
                width: 0,
                height: 0,
              });
              update?.();
            }
          }

          setInstruction({ ...instruction, blockedReason: args.self.data.blockReason });
        },
        onDragLeave: () => setInstruction(null),
        onDrop: () => setInstruction(null),
      }),
    );
  }, [node, depth, lastInGroup, dragElement, dropElement, block, update]);

  const hasChildren = node.children.length > 0;

  return (
    <>
      <Styled.Node ref={setDropElement} depth={depth} $instruction={instruction?.type}>
        {renderNode({ node, setDragElement, depth, hasChildren, instruction })}
      </Styled.Node>

      {hasChildren && node.expanded && (
        <>
          {node.children.map((child, index) => {
            return (
              <Node
                key={child.id}
                node={child}
                depth={depth + 1}
                lastInGroup={index === node.children.length - 1}
                block={block || isDragging}
                renderNode={renderNode}
                renderDragPreview={renderDragPreview}
              />
            );
          })}
        </>
      )}

      {dragState.type === 'preview' && createPortal(renderDragPreview({ node, instruction }), dragState.container)}

      {instruction?.type === 'instruction-blocked' && instruction.blockedReason && (
        <div
          ref={setTooltipEl}
          style={{ ...styles.popper, pointerEvents: 'none', zIndex: 9999 }}
          {...attributes.popper}>
          <Styled.Tooltip>{instruction.blockedReason}</Styled.Tooltip>
        </div>
      )}
    </>
  );
}
